import * as React from 'react';

/**
 * Toast state management for the Admin Console.
 * Manages a queue of toast notifications with auto-dismiss after 5 seconds.
 * Uses Context + useReducer pattern consistent with other state providers.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastItem {
  readonly id: string;
  readonly message: string;
  readonly variant: ToastVariant;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface ToastState {
  readonly toasts: readonly ToastItem[];
}

export const initialToastState: ToastState = {
  toasts: [],
};

// ─── Actions ─────────────────────────────────────────────────────────────────

export type ToastAction =
  | { readonly type: 'TOAST_ADD'; readonly payload: ToastItem }
  | { readonly type: 'TOAST_DISMISS'; readonly payload: string };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function toastReducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case 'TOAST_ADD':
      return { toasts: [...state.toasts, action.payload] };
    case 'TOAST_DISMISS':
      return { toasts: state.toasts.filter((t) => t.id !== action.payload) };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

export interface ToastContextValue {
  readonly toasts: readonly ToastItem[];
  readonly showToast: (message: string, variant: ToastVariant) => void;
  readonly dismissToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface ToastProviderProps {
  readonly children: React.ReactNode;
}

/**
 * ToastProvider manages toast notification queue with auto-dismiss.
 * Toasts automatically dismiss after 5 seconds.
 */
export function ToastProvider({ children }: ToastProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(toastReducer, initialToastState);
  const timersRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = React.useCallback((id: string): void => {
    dispatch({ type: 'TOAST_DISMISS', payload: id });
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = React.useCallback((message: string, variant: ToastVariant): void => {
    const id = crypto.randomUUID();
    const toast: ToastItem = { id, message, variant };
    dispatch({ type: 'TOAST_ADD', payload: toast });

    const timer = setTimeout(() => {
      dispatch({ type: 'TOAST_DISMISS', payload: id });
      timersRef.current.delete(id);
    }, 5000);
    timersRef.current.set(id, timer);
  }, []);

  // Cleanup timers on unmount
  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const contextValue = React.useMemo(
    (): ToastContextValue => ({
      toasts: state.toasts,
      showToast,
      dismissToast,
    }),
    [state.toasts, showToast, dismissToast],
  );

  return React.createElement(ToastContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access toast notifications and actions.
 * Must be used within a ToastProvider.
 */
export function useToast(): ToastContextValue {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
